import { useSelector } from 'react-redux'
import { useCheckKybMutation } from '../../services/guenoApi'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useMemo, useState } from 'react'
import { Box, Button, Typography } from '@mui/material'
import toaster from '../../toaster'
import { Accordion, AccordionDetails, AccordionSummary } from '../common/Accordion/Accordion'
import { Status } from '../utilities/Status'
import KybCheckDetail from '../../pages/kyb/components/KybCheckDetail'
import KybPreviousChecks from '../../pages/kyb/components/KybPreviousChecks'
import { LoadingButton } from '@mui/lab'
import { camelCaseFormatter } from '../utilities/formatters/camelCase'
import KeyboardDoubleArrowDownIcon from '@mui/icons-material/KeyboardDoubleArrowDown'
import KeyboardDoubleArrowUpIcon from '@mui/icons-material/KeyboardDoubleArrowUp'
import { kybNegativeCertificateChecks } from './kybCNJNegativeCertificateList'

const CNJNegativeCertificate = ({
  countrySegment,
  entityType,
  handleFetch,
  kyb,
  setOpenModalCheckStatus,
  openModalCheckStatus,
  setCheckId,
  setCheckStatus,
  setCheckType,
  currentCountry,
}) => {
  const { activeServices } = useSelector((state) => state.login)
  const { id } = useParams()
  const { t } = useTranslation(['common', 'messages'])
  const [createCheck, { isLoading: createCheckLoading, originalArgs: createCheckArgs }] = useCheckKybMutation()
  const [expandedMain, setExpandedMain] = useState([false])
  const [expanded, setExpanded] = useState({})

  const checks = useMemo(() => {
    if (!currentCountry) return []

    let sortedList = kybNegativeCertificateChecks[currentCountry][entityType]

    return sortedList
  }, [currentCountry])

  const handleCreateCheck = async (e, type) => {
    e.preventDefault()

    let data = {
      id: id,
      documentNumber: entityType === 'BUSINESS' ? kyb?.companyDetails?.documentNumber : kyb?.documentNumber,
      entity: entityType,
      type,
    }

    try {
      await createCheck({ countrySegment, body: data }).unwrap()
      toaster.success(t('messages:creationSuccess'))
    } catch (error) {
      console.error('[CREATE NEW COMMENT ERROR] --> ', error?.data?.data)
      toaster.error(error.message ? error.message : t('messages:creationError'))
    } finally {
      handleFetch()
    }
  }

  const handleChangeExpandedMain = (indexMain) => {
    const newState = { ...expandedMain }
    newState[indexMain] = !newState[indexMain]
    setExpandedMain(newState)
  }

  const handleChangeExpanded = (indexMain, index) => {
    const key = `${indexMain}-${index}`
    const newState = { ...expanded, [key]: !expanded[key] }
    setExpanded(newState)
  }

  return (
    <Box className="kyb-checks-flex">
      {checks?.map((mainChecks, indexMain) => (
        <Accordion
          key={indexMain}
          expanded={expandedMain[indexMain]}
          onChange={() => handleChangeExpandedMain(indexMain)}
        >
          <AccordionSummary expandIcon={null}>
            <Box className="accordion-wrapper">
              <Box className="accordion-status">
                <Typography variant="title">{t(`messages:kyb.checks.title.${mainChecks?.title}`)}</Typography>
                <Typography variant="subtitle3">{t(`messages:kyb.checks.subtitle.${mainChecks?.subtitle}`)}</Typography>
              </Box>
              <Button
                variant="outlinedBlank"
                onChange={(e) => {
                  e.stopPropagation()
                  return handleChangeExpandedMain(indexMain)
                }}
              >
                {expandedMain[indexMain] ? <KeyboardDoubleArrowDownIcon /> : <KeyboardDoubleArrowUpIcon />}
              </Button>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            {mainChecks?.items?.map((check, index) => {
              const key = `${indexMain}-${index}`
              return (
                <Accordion
                  key={key}
                  expanded={expanded[key] || false}
                  onChange={(e) => {
                    e.stopPropagation()
                    handleChangeExpanded(indexMain, index)
                  }}
                  variant="medium"
                  slotProps={{ transition: { unmountOnExit: true } }}
                >
                  <AccordionSummary expandIcon={null}>
                    <Box className="accordion-wrapper">
                      <Box className="accordion-status">
                        <Typography variant="text2">{t(`common:${check.title}`)}</Typography>
                        <Box>
                          {kyb?.[check?.name] ? (
                            <Box className="accordion-status-icon">
                              <Status
                                type={kyb[check.name].status}
                                noIcon={true}
                                sizeBig={true}
                                variant="colorSecondary"
                              />
                            </Box>
                          ) : (
                            <Box className="accordion-status-icon">
                              <Status type={'NOT_STARTED'} noIcon={true} sizeBig={true} variant="colorSecondary" />
                            </Box>
                          )}
                        </Box>
                      </Box>
                      <Button
                        variant="outlinedBlank"
                        onChange={(e) => {
                          e.stopPropagation()
                          return handleChangeExpanded(index)
                        }}
                      >
                        {expanded[index] ? <KeyboardDoubleArrowDownIcon /> : <KeyboardDoubleArrowUpIcon />}
                      </Button>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box>
                      {kyb?.[check?.name] ? (
                        <KybCheckDetail name={check.name} value={kyb[check.name]} />
                      ) : (
                        <Box>
                          <LoadingButton
                            size="small"
                            variant="contained"
                            color="secondary"
                            loading={!!createCheckLoading && createCheckArgs?.body?.type === check.key}
                            onClick={(e) => handleCreateCheck(e, check.key)}
                            disabled={!activeServices.includes('kybcheck')}
                          >
                            {t('common:run')} {camelCaseFormatter(check.name)}
                          </LoadingButton>
                        </Box>
                      )}
                      <Box className="kyb-close">
                        {kyb?.[check?.name] && (
                          <Box className="kyb-check-status">
                            <KybPreviousChecks
                              kybId={id}
                              type={check.key}
                              checks={checks}
                              countrySegment={countrySegment}
                            />
                            <Button
                              size="small"
                              fullWidth
                              onClick={() => {
                                setOpenModalCheckStatus(!openModalCheckStatus)
                                setCheckId(kyb[check.name]?._id)
                                setCheckStatus(kyb[check.name]?.status)
                                setCheckType(check.key)
                              }}
                              disabled={!activeServices.includes('kybcheck:checkId')}
                              variant="contained"
                            >
                              {t('common:changeStatus')}
                            </Button>
                            <LoadingButton
                              size="small"
                              fullWidth
                              variant="outlined"
                              loading={!!createCheckLoading && createCheckArgs?.body?.type === check.key}
                              onClick={(e) => handleCreateCheck(e, check.key)}
                              disabled={!activeServices.includes('kybcheck')}
                            >
                              {t('common:run')} {camelCaseFormatter(check.name)} {t('common:again')}
                            </LoadingButton>
                          </Box>
                        )}
                      </Box>
                    </Box>
                  </AccordionDetails>
                </Accordion>
              )
            })}
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  )
}

export default CNJNegativeCertificate
